import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useColors } from './shared';

const WEAKNESSES = [
  {
    label: 'Rook Endgames',
    eloCost: 42,
    detail:
      'You win only 28% of rook+pawn endgames where you’re up material — players at your rating convert 51%.',
    tip: 'Activate your rook before pushing the pawn. Drill the basics until it’s automatic.',
  },
  {
    label: 'Time Scrambles',
    eloCost: 31,
    detail:
      'Below 30 seconds on the clock, your blunder rate jumps from 6% to 24% — you’ve flagged 47 winning games this year.',
    tip: 'Pre-move more in clearly won positions. Practice 1-minute drills.',
  },
  {
    label: 'Isolated d-pawn',
    eloCost: 28,
    detail:
      'You accept an isolated d-pawn in 34% of your games and win only 38% of those. You rarely push d4-d5 at the right moment — in 71% of cases you let it get blockaded.',
    tip: 'Avoid early cxd5/exd5 exchanges. Study IQP middlegame plans.',
  },
  {
    label: 'Knight Placement',
    eloCost: 19,
    detail:
      'Your knights spend an average of 6.2 moves on the rim (a/h files) per game — 3x more than similar-rated players. You also miss 58% of outpost opportunities on e5/d5.',
    tip: 'Reroute rim knights immediately. Look for Nd2-f1-e3-d5 maneuvers.',
  },
  {
    label: 'Sicilian Defense',
    eloCost: 14,
    detail:
      'Your win rate with the Sicilian is 41% (vs 56% with 1...e5). You play 2...d6 but consistently mistime ...e5 — pushing it too early in 44% of games, allowing White a strong d5 bind.',
    tip: 'Switch to the Najdorf ...a6 setup or drop the Sicilian for 1...e5.',
  },
  {
    label: 'Back-rank Threats',
    eloCost: 8,
    detail:
      'You’ve been mated or lost decisive material to back-rank tactics 11 times this year. You create luft (h6/g6) in only 31% of middlegames.',
    tip: 'Low priority — but play h3/h6 earlier when castled and the position is quiet.',
  },
];
const MAX_COST = Math.max(...WEAKNESSES.map((w) => w.eloCost));
const TOTAL_ELO_COST = WEAKNESSES.reduce((s, w) => s + w.eloCost, 0);
WEAKNESSES.forEach((w) => {
  w.value = 0.25 + (w.eloCost / MAX_COST) * 0.75;
});

const isCritical = (w) => w.eloCost >= 25;

// Radar geometry. R is the radar shape's radius in viewBox units (200×200);
// bumping it makes the polygon larger relative to the text labels, which stay
// at the same fontSize in viewBox units (so text pixel size doesn't grow).
const R = 80;
const CX = 100;
const CY = 100;
const angleOf = (i) => (Math.PI * 2 * i) / WEAKNESSES.length - Math.PI / 2;

const radarPoints = WEAKNESSES.map((d, i) => {
  const a = angleOf(i);
  return `${CX + Math.cos(a) * R * d.value},${CY + Math.sin(a) * R * d.value}`;
}).join(' ');

// Per-dot positions in viewBox coordinates. Used by the radar's mousemove
// handler to find the nearest dot — eliminates dead hover zones between dots.
const RADAR_DOT_POSITIONS = WEAKNESSES.map((d, i) => {
  const a = angleOf(i);
  return {
    px: CX + Math.cos(a) * R * d.value,
    py: CY + Math.sin(a) * R * d.value,
  };
});

const gridPoints = (scale) =>
  WEAKNESSES.map((_, i) => {
    const a = angleOf(i);
    return `${CX + Math.cos(a) * R * scale},${CY + Math.sin(a) * R * scale}`;
  }).join(' ');

// ───── Icons — copied verbatim from DashboardLayout so the mockup matches the real product ─────
const ICONS = {
  home: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  weaknesses: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  strengths: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  ),
  archetype: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  ),
  openings: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
      <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
    </svg>
  ),
  progress: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  habits: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  rivals: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  training: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="12" y1="3" x2="12" y2="21" />
      <circle cx="8" cy="8" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="16" cy="16" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  ),
  settings: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
  signout: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  sync: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 4v6h-6" />
      <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
    </svg>
  ),
  // Knight crown — same as the dashboard's "Dashboard" indicator in AnalysisHeader.
  crown: (color) => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 3a3 3 0 00-3 3v1H9V6a3 3 0 00-6 0v7c0 5 3.5 8 9 8s9-3 9-8V6a3 3 0 00-3-3z" />
    </svg>
  ),
};

// Sidebar nav — exact label set + order from DashboardLayout.navItems, plus Settings at bottom.
const NAV_ITEMS = [
  { key: 'home', label: 'Home', icon: ICONS.home },
  { key: 'weaknesses', label: 'Weaknesses', icon: ICONS.weaknesses, active: true },
  { key: 'strengths', label: 'Strengths', icon: ICONS.strengths },
  { key: 'archetype', label: 'Archetype', icon: ICONS.archetype },
  { key: 'openings', label: 'Openings', icon: ICONS.openings },
  { key: 'progress', label: 'Progress', icon: ICONS.progress },
  { key: 'habits', label: 'Play Habits', icon: ICONS.habits },
  { key: 'rivals', label: 'Rivals', icon: ICONS.rivals },
  { key: 'training', label: 'Training Rooms', icon: ICONS.training },
];

const CARD_BG = '#16161C';
const SIDEBAR_BG = '#0F0F14';
const CARD_BORDER = '1px solid rgba(255,255,255,0.07)';
const ACCENT = '#8A74E4';

function HeroMockup() {
  const c = useColors();
  const bottomRowRef = useRef(null);
  const [radarVisible, setRadarVisible] = useState(false);
  // Rook Endgames is the default active weakness (highest Elo cost).
  // Hover overrides active for highlight only.
  const [activeIdx, setActiveIdx] = useState(0);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const highlightIdx = hoveredIdx !== null ? hoveredIdx : activeIdx;

  // Trigger the radar/bar/dot animations only when the bottom row (radar +
  // ranking) actually scrolls into view. Observing the bottom row instead of
  // the whole MacBook frame means the animations don't fire on initial load
  // (the bottom row is below the fold) — they wait for the user to scroll.
  useEffect(() => {
    if (!bottomRowRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRadarVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    obs.observe(bottomRowRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 2,
        px: { xs: 1.5, md: '2.5vw' },
        // Default (tall screens / widescreen monitors): aggressive overlap
        // so the mockup peeks generously under the CTA. On phones, sit
        // directly below the hero (no negative pull-up) so the gap to the
        // CTA is dictated by the hero's pb, matching the subtitle→CTA gap.
        mt: { xs: 0, md: 'max(-28vw, clamp(-580px, -40vh, -480px))' },
        // Shorter viewports (MacBook-class laptops): the hero text shrinks
        // via vh-bounded clamps in HeroSection, so the mockup needs to peek
        // less to keep the CTA clear of the laptop frame.
        //
        // Goal: place the mockup top a roughly CONSTANT buffer below the CTA at
        // every viewport height — so the mockup stays visible on short windows
        // while never touching the button.
        //
        // The hero is `min-height: 102vh`, but the CTA does NOT sit at 102vh:
        // the hero content (title/subtitle) scales with vh, so the CTA bottom
        // tracks at only ~41vh + ~111px. The mockup top = 102vh + mt, so to land
        // it at `CTA_bottom + ~64px buffer` we need
        //   mt = (41vh + 111 + 64) − 102vh ≈ 175px − 61vh.
        // On short windows this keeps the mockup peeking just under the CTA; on
        // tall windows it grows (clamped at −460px) for the generous peek.
        //
        // max(-28vw, …) additionally caps the pull-up by width: the MacBook
        // frame scales with viewport WIDTH (fixed aspect ratio), so on a narrow
        // window the -28vw term backs the (now-small) frame off to keep the
        // buffer; on wider windows the height term governs.
        '@media (max-height: 1200px) and (min-width: 900px)': {
          mt: 'max(-28vw, clamp(-460px, calc(175px - 61vh), -90px))',
        },
      }}
    >
      {/* MacBook frame wrapper — image is the bezel/body; content is positioned over the screen area. */}
      <Box
        sx={{
          position: 'relative',
          maxWidth: 1640,
          mx: 'auto',
          aspectRatio: '4096 / 3072',
          backgroundImage: `url('/macbook-frame.png?v=11')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      >
      <Box
        sx={{
          position: 'absolute',
          top: '12%',
          left: '7%',
          width: '86%',
          height: '73.35%',
          overflow: 'hidden',
          backgroundColor: '#131317',
          borderTopLeftRadius: { xs: '13px', md: '25px' },
          borderTopRightRadius: { xs: '13px', md: '25px' },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      {/* Scale wrapper — on small screens the desktop layout (sidebar +
          radar + ranking row) is too wide to fit the MacBook screen area.
          Width/height compensation (1 / scale factor) means children lay
          out at desktop dimensions; transform: scale then shrinks the
          rendered output to fit the actual screen rectangle. */}
      <Box
        sx={{
          width: { xs: '286%', sm: '167%', md: '100%' },
          height: { xs: '286%', sm: '167%', md: '100%' },
          transform: { xs: 'scale(0.35)', sm: 'scale(0.6)', md: 'none' },
          transformOrigin: 'top left',
          display: 'flex',
          flexDirection: 'column',
          flex: '0 0 auto',
        }}
      >
        {/* ============================================================ */}
        {/*  HEADER — mirrors AnalysisHeader: logo+title (left), crown    */}
        {/*  + Dashboard (center), settings + avatar + sign-out (right). */}
        {/* ============================================================ */}
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: { xs: 0.8, md: 1 },
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: 2,
            borderBottom: CARD_BORDER,
            backgroundColor: CARD_BG,
          }}
        >
          {/* ───── Left: traffic dots + logo + brand + page label below ───── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.2, md: 2 }, minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 0.8 }}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: ACCENT, opacity: 0.75 }}
                />
              ))}
            </Box>
            <Box
              component="img"
              src={c.isDark ? '/logo-light.svg' : '/logo.svg'}
              alt="Destrier logo"
              loading="lazy"
              sx={{ width: { xs: 29, md: 36 }, height: { xs: 29, md: 36 } }}
            />
            <Box
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.08rem', md: '1.3rem' },
                color: c.textPrimary,
                letterSpacing: '-0.02em',
              }}
            >
              Destrier
              <Box component="span" sx={{ color: ACCENT }}>
                .ai
              </Box>
            </Box>
          </Box>

          {/* ───── Center: crown + Dashboard ───── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: c.textMuted,
              justifySelf: 'center',
              '& svg': { width: 23, height: 23 },
            }}
          >
            {ICONS.crown(ACCENT)}
            <Box
              component="span"
              sx={{
                fontSize: { xs: '1.08rem', md: '1.3rem' },
                fontWeight: 700,
                color: c.textPrimary,
                letterSpacing: '-0.02em',
              }}
            >
              Dashboard
            </Box>
          </Box>

          {/* ───── Right: avatar + sign-out only (settings/sync/version live in the sidebar) ───── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, md: 1.2 }, justifySelf: 'end' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.8, py: 0.4, borderRadius: '12px' }}>
              <Box
                component="span"
                sx={{
                  display: 'block',
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: c.textPrimary,
                }}
              >
                Magnus
              </Box>
              <Box
                sx={{
                  width: { xs: 27, md: 31 },
                  height: { xs: 27, md: 31 },
                  borderRadius: '50%',
                  backgroundColor: ACCENT,
                  color: '#fff',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                M
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '10px',
                color: c.textMuted,
              }}
            >
              {ICONS.signout}
            </Box>
          </Box>
        </Box>

        {/* ============================================================ */}
        {/*  BODY — sidebar (icons + labels) + content                    */}
        {/* ============================================================ */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* ───── Sidebar (expanded with labels, matches DashboardLayout) ───── */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
              width: 220,
              py: 2,
              px: 1,
              backgroundColor: SIDEBAR_BG,
              borderRight: CARD_BORDER,
              gap: 0.5,
            }}
          >
            {NAV_ITEMS.map((item) => (
              <Box
                key={item.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  height: 44,
                  px: 1.5,
                  borderRadius: '12px',
                  color: item.active ? ACCENT : c.textMuted,
                  backgroundColor: item.active ? `${ACCENT}1F` : 'transparent',
                  '& svg': { width: 22, height: 22 },
                }}
              >
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22 }}>
                  {item.icon}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.95rem',
                    fontWeight: item.active ? 600 : 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.label}
                </Box>
              </Box>
            ))}

            {/* Bottom block — Settings, Sync games, Destrier branding (matches DashboardLayout). */}
            <Box sx={{ flex: 1 }} />
            <Box sx={{ borderTop: CARD_BORDER, pt: 1, mx: -1, px: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {/* Settings */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  height: 44,
                  px: 1.5,
                  borderRadius: '12px',
                  color: c.textMuted,
                  '& svg': { width: 22, height: 22 },
                }}
              >
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22 }}>
                  {ICONS.settings}
                </Box>
                <Box component="span" sx={{ fontSize: '0.95rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  Settings
                </Box>
              </Box>
              {/* Sync games */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  height: 44,
                  px: 1.5,
                  borderRadius: '12px',
                  color: c.textMuted,
                  '& svg': { width: 22, height: 22 },
                }}
              >
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22 }}>
                  {ICONS.sync}
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    lineHeight: 1.25,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Box component="span" sx={{ fontSize: '0.9rem', fontWeight: 500, color: c.textMuted }}>
                    Sync games
                  </Box>
                  <Box component="span" sx={{ fontSize: '0.78rem', color: c.textMuted, opacity: 0.75 }}>
                    Today 14:32
                  </Box>
                </Box>
              </Box>
              {/* Destrier branding + version */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  height: 44,
                  px: 1.5,
                  color: c.textMuted,
                  '& svg': { width: 22, height: 22 },
                }}
              >
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22 }}>
                  {ICONS.crown(ACCENT)}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Box component="span" sx={{ color: ACCENT }}>Destrier</Box>
                  <Box component="span" sx={{ ml: 0.6, color: c.textMuted, fontWeight: 400 }}>v0.1</Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ───── Right: greeting + radar + weakness list ───── */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1.4, md: 2 },
              p: { xs: 1.6, md: 2.5 },
            }}
          >
            {/* Page title — sits outside the greeting card. */}
            <Box
              component="h1"
              sx={{
                m: 0,
                color: '#2D2D35',
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                fontWeight: 900,
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
              }}
            >
              Weakness Analysis
            </Box>

            {/* Greeting card */}
            <Box
              sx={{
                borderRadius: '14px',
                backgroundColor: CARD_BG,
                border: CARD_BORDER,
                px: { xs: 2.4, md: 4 },
                py: { xs: 2.6, md: 3.5 },
              }}
            >
              <Box
                sx={{
                  color: c.textPrimary,
                  fontSize: { xs: '0.95rem', md: 'clamp(0.95rem, 1vw, 1.15rem)' },
                  lineHeight: 1.55,
                  fontWeight: 400,
                  letterSpacing: '-0.005em',
                }}
              >
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Hi Magnus,
                </Box>
                <br />
                Destrier analyzed your{' '}
                <Box component="strong" sx={{ color: c.textPrimary, fontWeight: 700 }}>
                  3,245 games
                </Box>{' '}
                and found{' '}
                <Box component="strong" sx={{ color: '#E4446E', fontWeight: 700 }}>
                  {WEAKNESSES.length} main weaknesses
                </Box>
                , costing you an estimated{' '}
                <Box component="strong" sx={{ color: '#E4446E', fontWeight: 700 }}>
                  −{TOTAL_ELO_COST} Elo
                </Box>{' '}
                per 100 games.
              </Box>
            </Box>

            {/* Bottom row: radar + weakness list, each with its title outside the card */}
            <Box
              ref={bottomRowRef}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                flex: 1,
                minHeight: 0,
              }}
            >
              {/* Radar card */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  overflow: 'hidden',
                  borderRadius: '14px',
                  backgroundColor: CARD_BG,
                  border: CARD_BORDER,
                  p: { xs: 2.6, md: 3.6 },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  component="h2"
                  sx={{
                    m: 0,
                    mb: 1,
                    fontSize: '1.15rem',
                    color: '#2D2D35',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    lineHeight: 1.2,
                  }}
                >
                  Weakness Radar
                </Box>
                <style>{`
                  @keyframes hm-radarGrow {
                    from { transform: scale(0); opacity: 0; }
                    to   { transform: scale(1); opacity: 1; }
                  }
                  @keyframes hm-pointFadeIn {
                    from { opacity: 0; transform: scale(0); }
                    to   { opacity: 1; transform: scale(1); }
                  }
                  @keyframes hm-barGrow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
                  .hm-radar-shape { transform-origin: ${CX}px ${CY}px; }
                  .hm-radar-shape.visible { animation: hm-radarGrow 0.9s ease forwards; }
                  .hm-radar-shape:not(.visible) { transform: scale(0); opacity: 0; }
                  .hm-radar-point.visible {
                    opacity: 0;
                    transform: scale(0);
                    animation: hm-pointFadeIn 0.55s cubic-bezier(0.34, 1.25, 0.64, 1) forwards;
                  }
                  .hm-radar-point:not(.visible) { opacity: 0; transform: scale(0); }
                  .hm-bar-fill {
                    transform-origin: left center;
                    transform: scaleX(0);
                    will-change: transform;
                  }
                  .hm-bar-fill.visible {
                    animation: hm-barGrow 1.7s cubic-bezier(0.22,0.9,0.3,1) forwards;
                  }
                `}</style>
                <Box
                  sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  onMouseLeave={() => setHoveredIdx(null)}
                >
                  <svg
                    width="100%"
                    viewBox="0 0 200 200"
                    fill="none"
                    style={{ overflow: 'visible', maxHeight: 460 }}
                    onMouseMove={(e) => {
                      const svg = e.currentTarget;
                      const rect = svg.getBoundingClientRect();
                      if (!rect.width || !rect.height) return;
                      // Map cursor → SVG viewBox coords (0..200).
                      const x = ((e.clientX - rect.left) / rect.width) * 200;
                      const y = ((e.clientY - rect.top) / rect.height) * 200;
                      let minDist = Infinity;
                      let nearest = 0;
                      for (let i = 0; i < RADAR_DOT_POSITIONS.length; i++) {
                        const p = RADAR_DOT_POSITIONS[i];
                        const dx = p.px - x;
                        const dy = p.py - y;
                        const d2 = dx * dx + dy * dy;
                        if (d2 < minDist) { minDist = d2; nearest = i; }
                      }
                      if (nearest !== hoveredIdx) setHoveredIdx(nearest);
                    }}
                  >
                    {[0.25, 0.5, 0.75, 1].map((s) => (
                      <polygon
                        key={s}
                        points={gridPoints(s)}
                        fill="none"
                        stroke={c.textMuted}
                        strokeWidth="0.4"
                        opacity="0.22"
                      />
                    ))}
                    {WEAKNESSES.map((_, i) => {
                      const a = angleOf(i);
                      return (
                        <line
                          key={i}
                          x1={CX}
                          y1={CY}
                          x2={CX + Math.cos(a) * R}
                          y2={CY + Math.sin(a) * R}
                          stroke={c.textMuted}
                          strokeWidth="0.3"
                          opacity="0.22"
                        />
                      );
                    })}
                    <polygon
                      className={`hm-radar-shape${radarVisible ? ' visible' : ''}`}
                      points={radarPoints}
                      fill={`${c.ctaBlue}26`}
                      stroke={c.ctaBlue}
                      strokeWidth="2"
                    />
                    {WEAKNESSES.map((d, i) => {
                      const a = angleOf(i);
                      const px = CX + Math.cos(a) * R * d.value;
                      const py = CY + Math.sin(a) * R * d.value;
                      // Per-label radial offset + explicit dx/dy nudge.
                      // Index order matches WEAKNESSES.
                      const LABEL_TWEAKS = [
                        { off: 22, dx: 0,  dy: 0 },   // Rook Endgames
                        { off: 12, dx: 0,  dy: -10 }, // Time Scrambles — push up
                        { off: 6,  dx: 0,  dy: 10 },  // Isolated d-pawn — push down
                        { off: 0,  dx: 0,  dy: 0 },   // Knight Placement
                        { off: 10, dx: 0,  dy: 0 },   // Sicilian Defense
                        { off: 8,  dx: 6,  dy: 0 },   // Back-rank Threats — more right
                      ];
                      const t = LABEL_TWEAKS[i];
                      const lx = CX + Math.cos(a) * (R + t.off) + t.dx;
                      const ly = CY + Math.sin(a) * (R + t.off) + t.dy;
                      const critical = isCritical(d);
                      const isHigh = i === highlightIdx;
                      const dotColor = critical ? '#E4446E' : c.ctaBlue;
                      return (
                        <g
                          key={i}
                          className={`hm-radar-point${radarVisible ? ' visible' : ''}`}
                          onClick={() => setActiveIdx(activeIdx === i ? null : i)}
                          style={{
                            cursor: 'pointer',
                            // Each dot pops in scaling from 0→1 around its own
                            // position (so the label translates outward with
                            // it), with a clockwise stagger.
                            transformOrigin: `${px}px ${py}px`,
                            animationDelay: `${0.6 + i * 0.05}s`,
                          }}
                        >
                          {/* Invisible hit-area for the dot */}
                          <circle cx={px} cy={py} r="18" fill="transparent" />
                          {isHigh && (
                            <circle
                              cx={px}
                              cy={py}
                              r={critical ? 11 : 9}
                              fill={dotColor}
                              opacity="0.16"
                            />
                          )}
                          <circle cx={px} cy={py} r={isHigh ? 5.5 : 3.4} fill={dotColor} style={{ transition: 'r 0.2s' }} />
                          {critical && !isHigh && (
                            <circle
                              cx={px}
                              cy={py}
                              r="6.5"
                              fill="none"
                              stroke="#E4446E"
                              strokeWidth="1"
                              opacity="0.4"
                            />
                          )}
                          {/* Invisible hit-area for the label */}
                          <circle cx={lx} cy={ly} r="14" fill="transparent" />
                          <text
                            x={lx}
                            y={ly}
                            fill={dotColor}
                            fontSize={isHigh ? '8' : '7'}
                            fontWeight={isHigh || critical ? 700 : 600}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            opacity={isHigh || critical ? 1 : 0.85}
                            style={{ transition: 'font-size 0.2s, opacity 0.2s' }}
                          >
                            {d.label}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                </Box>
              </Box>

              {/* Weakness list card */}
              <Box
                sx={{
                  flex: 1.05,
                  minWidth: 0,
                  minHeight: 0,
                  overflow: 'hidden',
                  borderRadius: '14px',
                  backgroundColor: CARD_BG,
                  border: CARD_BORDER,
                  p: { xs: 2.6, md: 3.6 },
                  display: 'flex',
                  flexDirection: 'column',
                  }}
                >
                <Box
                  component="h2"
                  sx={{
                    m: 0,
                    mb: 1.5,
                    fontSize: '1.15rem',
                    color: '#2D2D35',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    lineHeight: 1.2,
                  }}
                >
                  Weakness Ranking
                </Box>

                {/* No `gap` here — adjacent items touch via their padding so
                    there's no dead hover zone between them. */}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {WEAKNESSES.map((w, i) => {
                    const critical = isCritical(w);
                    const accent = critical ? '#E4446E' : c.ctaBlue;
                    const isActive = activeIdx === i;
                    const isHigh = i === highlightIdx;
                    return (
                      <Box
                        key={i}
                        onClick={() => setActiveIdx(activeIdx === i ? null : i)}
                        onMouseEnter={() => setHoveredIdx(i)}
                        onMouseLeave={() => setHoveredIdx(null)}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.4,
                          opacity: isHigh ? 1 : 0.92,
                          cursor: 'pointer',
                          p: 1.2,
                          mx: -1.2,
                          borderRadius: '10px',
                          backgroundColor: isHigh ? `${accent}10` : 'transparent',
                          transition: 'background-color 0.2s, opacity 0.2s',
                        }}
                      >
                        <Box
                          sx={{
                            flex: '0 0 auto',
                            mt: 0.7,
                            width: 9,
                            height: 9,
                            borderRadius: '50%',
                            bgcolor: accent,
                            boxShadow: critical ? `0 0 8px ${accent}90` : 'none',
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'baseline',
                              justifyContent: 'space-between',
                              gap: 1,
                              mb: 0.7,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                color: c.textPrimary,
                                fontSize: { xs: '0.95rem', md: 'clamp(1rem, 1.05vw, 1.15rem)' },
                                fontWeight: 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {w.label}
                            </Box>
                            <Box
                              component="span"
                              sx={{
                                color: accent,
                                fontSize: { xs: '1rem', md: 'clamp(1rem, 1.15vw, 1.2rem)' },
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              −{w.eloCost} Elo
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              height: 4,
                              borderRadius: '3px',
                              bgcolor: 'rgba(255,255,255,0.06)',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              className={`hm-bar-fill${radarVisible ? ' visible' : ''}`}
                              style={{
                                height: '100%',
                                width: `${(w.eloCost / MAX_COST) * 100}%`,
                                backgroundColor: accent,
                                opacity: 0.85,
                                // Synced with the radar grow-in: starts at 0s,
                                // small per-item stagger for the wave effect.
                                animationDelay: `${i * 0.08}s`,
                              }}
                            />
                          </Box>
                          {/* Expanded detail + tip — animates open AND closed via
                              AnimatePresence so swapping active rows doesn't show the
                              old block snap-collapse before the new one expands. */}
                          <AnimatePresence initial={false}>
                            {isActive && (
                              <Box
                                component={motion.div}
                                key="expand"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.28, ease: [0.22, 0.9, 0.3, 1] }}
                                sx={{ overflow: 'hidden' }}
                              >
                                <Box sx={{ pt: 1.2 }}>
                                  <Box
                                    sx={{
                                      color: c.textSecondary,
                                      fontSize: { xs: '0.95rem', md: '1rem' },
                                      lineHeight: 1.55,
                                      mb: 1,
                                      fontWeight: 400,
                                    }}
                                  >
                                    {w.detail}
                                  </Box>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: 1,
                                      pt: 0.4,
                                    }}
                                  >
                                    <Box
                                      component="svg"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke={accent}
                                      strokeWidth="1.8"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      sx={{
                                        width: 18,
                                        height: 18,
                                        flexShrink: 0,
                                        mt: '3px',
                                      }}
                                    >
                                      <path d="M9 18h6" />
                                      <path d="M10 22h4" />
                                      <path d="M12 2a7 7 0 00-4 12.7c.6.5 1 1.2 1 2V17h6v-.3c0-.8.4-1.5 1-2A7 7 0 0012 2z" />
                                    </Box>
                                    <Box
                                      sx={{
                                        color: c.textPrimary,
                                        fontSize: { xs: '0.95rem', md: '1rem' },
                                        lineHeight: 1.5,
                                        fontWeight: 400,
                                      }}
                                    >
                                      <Box component="span" sx={{ color: accent, fontWeight: 600 }}>
                                        Tip —{' '}
                                      </Box>
                                      {w.tip}
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </AnimatePresence>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      </Box>
        {/* Subtle diagonal glare band across the LCD area. */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: '10.51%',
            left: '5.76%',
            width: '88.60%',
            height: '75%',
            pointerEvents: 'none',
            borderTopLeftRadius: { xs: '15px', md: '30px' },
            borderTopRightRadius: { xs: '15px', md: '30px' },
            overflow: 'hidden',
            background:
              'linear-gradient(195deg, rgba(255,255,255,0.008) 0%, rgba(255,255,255,0.022) 30%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.022) 70%, rgba(255,255,255,0.008) 100%)',
          }}
        />
      </Box>
    </Box>
  );
}

export default HeroMockup;
